import { ThirdPartyAppCredentialsPopupDetails } from '@coach-bot/data-access/shared/interfaces';
import { StudentThirdPartyAppCredentials } from '@coach-bot/data-access/shared/models/student-third-party-app-credentials.model';
import { isRestrictedEmail } from '@coach-bot/data-access/shared/utils/permissions';
import { ContentScriptAction, ContextMenuAction } from '@shared/extension/enums';
import { ContextMenuActionData } from '@shared/extension/interfaces/context-menu-action-data.interface';
import { safeSendChromeMessageToTab } from '@shared/extension/utils/events';
import { getActiveTab, getActiveValidTab, isValidTab } from '@shared/extension/utils/tabs';
import { LANGUAGES } from '@shared/translate/constants/languages';
import { CURRENT_LANGUAGE_LOCAL_STORAGE_KEY } from '@shared/translate/constants/storage';
import { Language } from '@shared/translate/enums/language.enum';
import { LanguageData } from '@shared/translate/interfaces/language.interface';
import { getRawTranslation } from '@shared/translate/utils/get-raw-translation';
import { LEARNING_APPS_WITH_CREDENTIALS, getLearningApp } from '@shared/utils';
import { getLocalStorage, getThirdPartyAppCredentialsPopupDetails, setLocalStorage } from '../utils';
import { Services } from './services.service';

const PREFIX = '• ';

export class ContextMenuService {
  private updatingActions: boolean = false;

  constructor(private services: Services) {}

  async updateContextMenuActions(): Promise<void> {
    if (!this.updatingActions) {
      this.updatingActions = true;
      await this.handleUpdateContextMenuActions();
      this.updatingActions = false;
    }
  }

  async handleContextMenuClicked(info: chrome.contextMenus.OnClickData, tab: chrome.tabs.Tab): Promise<void> {
    if (!tab || !tab.id) {
      return;
    }
    try {
      const actionData: ContextMenuActionData | undefined = this.getActionData(info.menuItemId);
      if (!actionData) {
        return;
      }
      if (actionData.id.startsWith(ContextMenuAction.ChangeLanguageOption)) {
        const language: string = actionData.id.split('_')[1];
        await setLocalStorage(CURRENT_LANGUAGE_LOCAL_STORAGE_KEY, language);
        this.updateContextMenuActions();
      } else if (actionData.id === ContextMenuAction.OpenDashboard) {
        chrome.tabs.create({ url: this.services.environment.dashboardUrl, active: true });
      } else if (actionData.id === ContextMenuAction.SignOut) {
        await this.services.authService.signOut();
      } else if (actionData.id.startsWith(ContextMenuAction.ViewCredentialsOption)) {
        await this.handleViewCredentialsOption(actionData);
      }
    } catch (error) {
      console.error('Error handling context menu click:', error);
    }
  }

  setExtensionIcon(path: string): void {
    chrome.action.setIcon({ path });
  }

  setExtensionName(name: string): void {
    chrome.action.setTitle({ title: name });
  }

  /**
   * Check actions
   */

  private async handleUpdateContextMenuActions(): Promise<void> {
    const language: Language | undefined = await getLocalStorage(CURRENT_LANGUAGE_LOCAL_STORAGE_KEY);
    const actions: ContextMenuActionData[] = [];

    // await this.checkOpenDashboardAction(actions, language);
    // await this.checkChangeLanguageActions(actions, language);
    await this.checkViewCredentialsActions(actions, language);
    await this.checkSignOutAction(actions, language);

    chrome.contextMenus.removeAll();
    await this.services.commonStore.setContextMenuActions(actions);
    actions.forEach((actionData: ContextMenuActionData) => {
      chrome.contextMenus.create({
        id: actionData.id,
        title: actionData.title,
        contexts: ['all'],
        parentId: actionData.parentId,
      });
    });
  }

  private async checkOpenDashboardAction(actions: ContextMenuActionData[], language: Language): Promise<void> {
    if (!isRestrictedEmail(this.services.commonStore.profileEmail$.value)) {
      const text: string = await getRawTranslation('extension.context_menu.open_dashboard', language);
      actions.push({
        id: ContextMenuAction.OpenDashboard,
        title: `${PREFIX} ${text}`,
      });
    }
  }

  private async checkChangeLanguageActions(actions: ContextMenuActionData[], language: Language): Promise<void> {
    const text: string = await getRawTranslation('extension.context_menu.change_language', language);
    actions.push({
      id: ContextMenuAction.ChangeLanguage,
      title: `${PREFIX} ${text}`,
    });
    LANGUAGES.forEach((languageData: LanguageData) => {
      actions.push({
        id: ContextMenuAction.ChangeLanguageOption + '_' + languageData.code,
        parentId: ContextMenuAction.ChangeLanguage,
        title: `${languageData.flagSymbol} ${languageData.nativeName}`,
      });
    });
  }

  private async checkViewCredentialsActions(actions: ContextMenuActionData[], language: Language): Promise<void> {
    const credentialsList: StudentThirdPartyAppCredentials[] | null =
      this.services.studentStore.thirdPartyAppCredentialsList$.value;
    const filteredCredentialsList: StudentThirdPartyAppCredentials[] =
      credentialsList?.filter((credentials: StudentThirdPartyAppCredentials) =>
        LEARNING_APPS_WITH_CREDENTIALS.includes(credentials.appName)
      ) || [];

    if (!this.services.commonStore.isSignedIn$.value || !filteredCredentialsList.length) {
      return;
    }

    try {
      const activeTab: chrome.tabs.Tab | null = await getActiveTab();
      if (!activeTab || !isValidTab(activeTab) || !activeTab.url) {
        return;
      }
      const learningApp: string | undefined = getLearningApp(activeTab.url);
      if (!LEARNING_APPS_WITH_CREDENTIALS.includes(learningApp)) {
        return;
      }
      const text: string = await getRawTranslation('extension.context_menu.view_credentials', language);
      actions.push({
        id: ContextMenuAction.ViewCredentials,
        title: `${PREFIX} ${text}`,
      });
      credentialsList.forEach((credentials: StudentThirdPartyAppCredentials) => {
        actions.push({
          id: ContextMenuAction.ViewCredentialsOption + '_' + credentials.appName,
          parentId: ContextMenuAction.ViewCredentials,
          title: credentials.appName,
        });
      });
    } catch (error) {
      console.error('Error checking view credentials actions:', error);
    }
  }

  private async checkSignOutAction(actions: ContextMenuActionData[], language: Language): Promise<void> {
    if (this.services.commonStore.isSignedIn$.value) {
      const text: string = await getRawTranslation('extension.context_menu.sign_out', language);
      actions.push({
        id: ContextMenuAction.SignOut,
        title: `${PREFIX} ${text}`,
      });
    }
  }

  /**
   * Handlers
   */

  private async handleViewCredentialsOption(actionData: ContextMenuActionData | undefined): Promise<void> {
    const appName: string = actionData.id.split('_')[1];
    const credentials: StudentThirdPartyAppCredentials | undefined =
      this.services.studentStore.thirdPartyAppCredentialsList$.value.find(
        (item: StudentThirdPartyAppCredentials) => item.appName === appName
      );
    if (credentials) {
      const activeTab: chrome.tabs.Tab | null = await getActiveValidTab();
      if (activeTab) {
        const popupDetails: ThirdPartyAppCredentialsPopupDetails = await getThirdPartyAppCredentialsPopupDetails(
          appName,
          false,
          this.services
        );
        safeSendChromeMessageToTab(activeTab.id!, {
          action: ContentScriptAction.OpenThirdPartyAppCredentialsPopup,
          detail: { credentials, popupDetails },
        });
      }
    }
  }

  /**
   * Utils
   */

  private getActionData(menuItemId: number | string): ContextMenuActionData | undefined {
    return this.services.commonStore.contextMenuActions$.value.find(action => action.id === menuItemId.toString());
  }
}
